import clsx from 'clsx';
import React, {
  memo, useRef, useState, useEffect, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import './horizontal-tabs-container.scss';
import { HorizontalTabScrollTypes, HorizontalTabTypes } from './constants';
import HorizontalTab from './views/horizontal-tab/horizontal-tab';
import { getTabsParentClass } from './utils';
import ScrollButton from './views/scroll-button/scroll-button';
import ActionButton from './views/action-button/action-button';

const HorizontalTabsContainer = ({
  type, tabs, onClick, scrollType, activeTabId, actionButtons,
}) => {
  const tabsContainerRef = useRef();
  const [tabComponent, setTabComponent] = useState(null);
  const [isGroupedTabs, setIsGroupedTabs] = useState(false);
  const [expandedTabIdsMap, setExpandedTabIdsMap] = useState({});
  const [scrollButtonStatus, setScrollButtonStatus] = useState({});
  const [selectedTabId, setSelectedTabId] = useState(activeTabId || tabs[0]?.id);

  const getScrollButtonStatus = ({ scrollLeft, scrollWidth, clientWidth }) => ({
    leftDisabled: scrollLeft <= 0,
    visible: clientWidth !== scrollWidth,
    rightDisabled: scrollLeft + clientWidth >= scrollWidth,
  });

  useEffect(() => {
    setScrollButtonStatus(getScrollButtonStatus(tabsContainerRef.current || {}));
  }, [expandedTabIdsMap]);

  useEffect(() => {
    setSelectedTabId(activeTabId || tabs[0]?.id);
    setTabComponent(tabs[0]?.component || null);
  }, [activeTabId]);

  useEffect(() => {
    let isGrouped = false;
    let expandedIdsMap = {};
    const tempParentIdMap = {};

    const fillActiveTabMap = (list, parentId = '') => {
      for (let itr = 0; itr < list.length; itr += 1) {
        const { id, children } = list[itr];

        if (selectedTabId === id) {
          expandedIdsMap = tempParentIdMap[parentId];
          break;
        }

        if (!parentId) {
          tempParentIdMap[id] = { [id]: true };
        } else if (children?.length > 0) {
          tempParentIdMap[parentId][id] = true;
        }

        if (children?.length > 0) {
          isGrouped = true;
          fillActiveTabMap(children, parentId || id);
        }
      }
    };
    fillActiveTabMap(tabs);
    setIsGroupedTabs(isGrouped);
    setExpandedTabIdsMap(expandedIdsMap || {});
  }, [tabs, selectedTabId]);

  const handleTabClick = useCallback((id, children = [], component = null) => {
    let newTabId = id;
    if (children?.length > 0) {
      newTabId = children[0].id;
    }

    setSelectedTabId(newTabId);
    setTabComponent(component);
    onClick(id);
  }, [onClick]);

  const handleScrollButtonClick = useCallback((side) => {
    const { scrollLeft, scrollWidth, clientWidth } = tabsContainerRef.current;
    const pixelToMove = clientWidth * 0.75;
    const left = side === 'left' ? scrollLeft - pixelToMove : scrollLeft + pixelToMove;

    tabsContainerRef.current.scrollTo({
      left,
      behavior: 'smooth',
    });
    setScrollButtonStatus(getScrollButtonStatus({
      scrollWidth,
      clientWidth,
      scrollLeft: scrollLeft + left,
    }));
  }, []);

  const getTab = ({
    id, label, disabled, renderIcon, badgeProps, component,
  }, hasParent = false, children = [], onlySelectedChild = false) => (
    <HorizontalTab
      id={id}
      type={type}
      label={label}
      disabled={disabled}
      hasParent={hasParent}
      component={component}
      badgeProps={badgeProps}
      childrenList={children}
      renderIcon={renderIcon}
      onClick={handleTabClick}
      isGroupedTabs={isGroupedTabs}
      selected={selectedTabId === id}
      expanded={expandedTabIdsMap[id]}
      onlySelectedChild={onlySelectedChild}
    />
  );

  const getTabs = (list, hasParent = false) => (
    list.map(({ children, ...tab }, index) => {
      const hasChildren = children?.length > 0 && expandedTabIdsMap[tab.id];
      const parentClass = getTabsParentClass(type, hasChildren);
      const isLastChild = list.length - 1 === index;
      const onlySelectedChild = !hasParent && !hasChildren && isGroupedTabs
        && selectedTabId === tab.id;

      return (
        <div className={clsx(parentClass, { 'no-child': onlySelectedChild })} key={tab.id}>
          {getTab(tab, hasParent, children, onlySelectedChild)}
          {hasChildren && getTabs(children, true)}
          {isGroupedTabs && !hasChildren && !hasParent && !isLastChild && (
            <span className="separator" />
          )}
        </div>
      );
    }));

  return (
    <div className="horizontal-tabs-cont">
      <div className="htc-wrapper">
        {scrollButtonStatus?.visible && (
          <ScrollButton
            side="left"
            type={scrollType}
            onClick={handleScrollButtonClick}
            disabled={scrollButtonStatus?.leftDisabled}
          />
        )}
        <div ref={tabsContainerRef} className={clsx('htc-tabs-cont', type)}>
          {getTabs(tabs, false)}
        </div>
        {scrollButtonStatus?.visible && (
          <ScrollButton
            side="right"
            type={scrollType}
            onClick={handleScrollButtonClick}
            disabled={scrollButtonStatus?.rightDisabled}
          />
        )}
        {actionButtons.length > 0 && (
          <div className="action-button-wrapper">
            <span className="abw-separator" />
            {actionButtons.map(({
              id, size, type: buttonType, title, label, disabled, onClick: buttonClick, className,
              renderIcon, borderLess, appearance, badgeProps,
            }) => (
              <ActionButton
                id={id}
                key={id}
                size={size}
                label={label}
                title={title}
                type={buttonType}
                disabled={disabled}
                onClick={buttonClick}
                className={className}
                borderLess={borderLess}
                renderIcon={renderIcon}
                appearance={appearance}
                badgeProps={badgeProps}
              />
            ))}
          </div>
        )}
      </div>
      {tabComponent}
    </div>
  );
};

HorizontalTabsContainer.defaultProps = {
  tabs: [],
  activeTabId: '',
  onClick: () => {},
  actionButtons: [],
  type: HorizontalTabTypes.TEXT_TAB,
  scrollType: HorizontalTabScrollTypes.CHEVRON_OUTLINED,
};

HorizontalTabsContainer.propTypes = {
  onClick: PropTypes.func,
  activeTabId: PropTypes.string,
  tabs: PropTypes.arrayOf(PropTypes.shape({
    ...HorizontalTab.propTypes,
    id: PropTypes.string,
    component: PropTypes.node,
  })),
  actionButtons: PropTypes.arrayOf(PropTypes.shape({
    ...ActionButton.propTypes,
  })),
  type: PropTypes.oneOf(Object.values(HorizontalTabTypes)),
  scrollType: PropTypes.oneOf(Object.values(HorizontalTabScrollTypes)),
};

export default memo(HorizontalTabsContainer);
